import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys, invalidateQueries } from '../lib/queryKeys';
import { getApiErrorMessage } from '../lib/apiErrors';
import type { 
  CreateCustomerRequest, 
  UpdateCustomerRequest, 
  CustomerFilterRequest
} from '../types/customers';

// Get customers list with optional filters
export function useCustomers(
  filters?: CustomerFilterRequest,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: filters ? queryKeys.customers.list(filters) : queryKeys.customers.lists(),
    queryFn: async () => {
      const response = await apiService.getCustomers(filters);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch customers'));
      }
      // Backend returns CustomerListResponse
      return response.data || { customers: [], totalCount: 0, page: 1, pageSize: 20, totalPages: 0 };
    },
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

// Get single customer by ID
export function useCustomer(customerId: string) {
  return useQuery({
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: async () => {
      const response = await apiService.getCustomer(customerId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch customer'));
      }
      return response.data;
    },
    enabled: !!customerId,
  });
}

// Get single customer by email (requires organizationId for multi-org support)
export function useCustomerByEmail(email: string, organizationId: string) {
  return useQuery({
    queryKey: ['customers', 'by-email', email, organizationId],
    queryFn: async () => {
      const response = await apiService.getCustomerByEmail(email, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch customer'));
      }
      return response.data;
    },
    enabled: !!email && !!organizationId,
  });
}

// Get customer statistics
export function useCustomerStats(organizationIds?: string[]) {
  return useQuery({
    queryKey: ['customers', 'stats', organizationIds],
    queryFn: async () => {
      const response = await apiService.getCustomerStats(organizationIds);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch customer stats'));
      }
      return response.data;
    },
  });
}

// Get customer tickets
export function useCustomerTickets(customerId: string) {
  return useQuery({
    queryKey: ['customers', customerId, 'tickets'],
    queryFn: async () => {
      const response = await apiService.getCustomerTickets(customerId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch customer tickets'));
      }
      return response.data;
    },
    enabled: !!customerId,
  });
}

// Create customer mutation (requires organizationId for multi-org support)
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ customer, organizationId }: { customer: CreateCustomerRequest; organizationId: string }) => {
      const response = await apiService.createCustomer(customer, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to create customer'));
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate customers list and stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.customers.lists() });
      queryClient.invalidateQueries({ queryKey: ['customers', 'stats'] });
    },
  });
}

// Update customer mutation
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: UpdateCustomerRequest 
    }) => {
      const response = await apiService.updateCustomer(id, updates);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update customer'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate specific customer and list
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.customers.lists() });
    },
  });
}

// Archive customer mutation (Admin in the customer's organization)
export function useArchiveCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      const response = await apiService.archiveCustomer(customerId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to archive customer'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.customers.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: invalidateQueries.customers.lists() });
      queryClient.invalidateQueries({ queryKey: ['customers', 'stats'] });
      // Archiving takes the customer off the CC list of active tickets
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.all() });
    },
  });
}

// Restore archived customer mutation (Admin in the customer's organization)
export function useRestoreCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      const response = await apiService.restoreCustomer(customerId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to restore customer'));
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.customers.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: invalidateQueries.customers.lists() });
      queryClient.invalidateQueries({ queryKey: ['customers', 'stats'] });
    },
  });
}

// Erase an archived customer's personal data (Admin in the customer's organization). Their tickets are purged
export function useEraseCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ customerId, confirmEmail }: { customerId: string; confirmEmail: string }) => {
      const response = await apiService.eraseCustomer(customerId, confirmEmail);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to erase the customer'));
      }
      return response.data;
    },
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.customers.lists() });
      queryClient.invalidateQueries({ queryKey: ['customers', 'stats'] });
      // Their tickets were permanently deleted, and their messages elsewhere removed
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'tickets'] });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.all() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.reports.all() });
    },
  });
}

// Permanently delete a customer with no ticket history (Admin in the customer's organization)
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      const response = await apiService.deleteCustomer(customerId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete customer'));
      }
      return response.data;
    },
    onSuccess: (_, customerId) => {
      // Invalidate customers list and stats
      queryClient.invalidateQueries({ queryKey: invalidateQueries.customers.lists() });
      queryClient.invalidateQueries({ queryKey: ['customers', 'stats'] });
      // Remove the specific customer from cache
      queryClient.removeQueries({ queryKey: queryKeys.customers.detail(customerId) });
    },
  });
}